// src/lib/arrivalsSource.ts
//
// Where «Llegadas» come from on Resumen and Instalaciones (product decision
// P02): the hotel's own reservation list when it shares one (without cancelled
// and no-show reservations), otherwise the reservations with activity in
// Roommatik. The screen names the figure accordingly and says which applies to
// each hotel.
import type { ArrivalsSource } from '@/types/analytics';

/** The label of the arrivals figure for the scope's overall source. */
export const arrivalsLabelKey = (source: ArrivalsSource | 'mixed' | undefined): string => {
  switch (source) {
    case 'roommatik':
      return 'resources.summary.tiles.arrivalsRoommatik';
    case 'mixed':
      return 'resources.summary.tiles.arrivalsMixed';
    default:
      return 'resources.summary.tiles.arrivals';
  }
};

/**
 * Cobros: the «N reservas con cobro · X % de …» sentence names the same arrivals
 * as Resumen (P02).
 */
export const paymentsArrivalsDetailKey = (source: ArrivalsSource | 'mixed' | undefined): string => {
  switch (source) {
    case 'roommatik':
      return 'tiles.amountDetailRoommatik';
    case 'mixed':
      return 'tiles.amountDetailMixed';
    default:
      return 'tiles.amountDetail';
  }
};

export interface ArrivalsSourceNote {
  key: string;
  params?: Record<string, string>;
}

/** Up to this many hotels, a mixed scope names each hotel under its source. */
export const NAMED_SOURCE_MAX_HOTELS = 4;

/**
 * The note under Resumen's figures: one sentence when every hotel uses the same
 * source; when they differ, the hotels of each source by name for a handful of
 * hotels, otherwise how many share their list (Instalaciones has the detail per
 * hotel). A hotel without a known name never surfaces as its id. Null without hotels.
 */
export const arrivalsSourceNote = (
  byHotel: Record<string, ArrivalsSource> | undefined,
  hotelName: (id: string) => string | undefined
): ArrivalsSourceNote | null => {
  const entries = Object.entries(byHotel ?? {});
  if (entries.length === 0) return null;
  const ofSource = (source: ArrivalsSource) => entries.filter(([, s]) => s === source).map(([id]) => id);
  const shared = ofSource('hotelSystem');
  const notShared = ofSource('roommatik');
  if (shared.length === 0 || notShared.length === 0) {
    return { key: shared.length ? 'resources.summary.source.hotelSystem' : 'resources.summary.source.roommatik' };
  }
  const named = (ids: string[]) => ids.map(hotelName);
  const sharedNames = named(shared);
  const notSharedNames = named(notShared);
  const allNamed = [...sharedNames, ...notSharedNames].every((n): n is string => !!n);
  if (entries.length <= NAMED_SOURCE_MAX_HOTELS && allNamed) {
    const list = (names: (string | undefined)[]) =>
      (names as string[]).sort((a, b) => a.localeCompare(b)).join(', ');
    return {
      key: 'resources.summary.source.mixed',
      params: { hotelSystem: list(sharedNames), roommatik: list(notSharedNames) },
    };
  }
  return {
    key: 'resources.summary.source.mixedCount',
    params: { count: String(shared.length), total: String(entries.length) },
  };
};
